import { getHandoffDeviceId } from '../interpreter-queue/browserDeviceId';
import { apiClient, unwrapApiResponse } from '../shared/api-client';
import { getSecureItem } from './secureStorage';
// Treat a token that expires within this window as already expired so a
// reload does not race the last seconds of its validity.
const ROOM_TOKEN_EXPIRY_SLACK_MS = 10000;
/**
 * Returns whether a Jitsi room JWT is expired (or about to expire), judged
 * only from its exp claim — no signature verification, which the client
 * cannot do anyway. Malformed tokens are treated as not expired so the
 * server remains the authority on rejecting them.
 */
export function isRoomTokenExpired(token, nowMs = Date.now()) {
    try {
        const payloadPart = token.split('.')[1];
        if (!payloadPart) {
            return false;
        }
        const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(normalized));
        return typeof payload.exp === 'number'
            && (payload.exp * 1000) <= nowMs + ROOM_TOKEN_EXPIRY_SLACK_MS;
    }
    catch {
        return false;
    }
}
/**
 * Removes an expired room JWT from a join URL. Room tokens live 15 minutes;
 * reloading a conference URL that still carries a dead token can never
 * succeed — prosody rejects it, the failure overlay reloads the same URL,
 * and the page loops forever (re-grabbing the camera on every cycle).
 * Stripping the stale token lets a signed-in user re-mint a fresh one during
 * navigation and lands an anonymous visitor on the guest/login path instead.
 */
export function stripExpiredRoomTokenFromURL(url) {
    const token = url.searchParams.get('jwt');
    if (!token || !isRoomTokenExpired(token)) {
        return url;
    }
    const cleaned = new URL(url.toString());
    cleaned.searchParams.delete('jwt');
    return cleaned;
}
/**
 * Returns whether a room JWT was minted for the room being joined, judged from
 * its room claim alone. A malformed token counts as non-matching so the caller
 * replaces it rather than trusting something it cannot read.
 */
export function roomTokenMatchesRoom(token, roomName) {
    try {
        const payloadPart = token.split('.')[1];
        if (!payloadPart) {
            return false;
        }
        const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(normalized)).room === roomName;
    }
    catch {
        return false;
    }
}
/** Presentation hint only: the media server still verifies the signature and room authority.
 * This never creates an application session or authorizes an API request.
 */
export function hasMediaOnlyRoomEntryToken(token, roomName, nowMs = Date.now()) {
    if (typeof token !== 'string' || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token))
        return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload || typeof payload !== 'object' || Array.isArray(payload))
            return false;
        const { room, exp, nbf, context } = payload;
        if (room !== roomName || typeof exp !== 'number' || !Number.isFinite(exp)
            || exp * 1000 <= nowMs + ROOM_TOKEN_EXPIRY_SLACK_MS
            || (nbf !== undefined && (typeof nbf !== 'number' || !Number.isFinite(nbf) || nbf * 1000 > nowMs))
            || !context || typeof context !== 'object' || Array.isArray(context))
            return false;
        const user = context.user;
        if (!user || typeof user !== 'object' || Array.isArray(user))
            return false;
        const { role, moderator } = user;
        return (role === 'guest' || role === 'observer') && (moderator === false || moderator === 'false');
    }
    catch {
        return false;
    }
}
/**
 * URL flag for "Move call here" / "Move it back here": mint the room token
 * with moveCall so the server moves the account's call to this device. It is
 * consumed by the mint and never left in the room URL.
 */
export const MOVE_CALL_PARAM = 'moveCall';
export function isMoveCallRequested(url) {
    return url.searchParams.get(MOVE_CALL_PARAM) === '1';
}
export function withoutMoveCall(url) {
    const cleaned = new URL(url.toString());
    cleaned.searchParams.delete(MOVE_CALL_PARAM);
    return cleaned;
}
export function withMoveCall(url) {
    const moved = new URL(url.toString());
    // A valid jwt would skip the mint; a move needs one.
    moved.searchParams.delete('jwt');
    moved.searchParams.set(MOVE_CALL_PARAM, '1');
    return moved;
}
export class RoomAuthorizationError extends Error {
    constructor(message, status, code, 
    /** 409 CALL_ACTIVE_ON_OTHER_DEVICE: which device holds the call. */
    hold) {
        super(message);
        this.status = status;
        this.code = code;
        this.hold = hold;
        this.name = 'RoomAuthorizationError';
    }
}
function holdFromErrorBody(body) {
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
        return undefined;
    }
    const hold = body.hold;
    if (!hold || typeof hold !== 'object' || Array.isArray(hold)) {
        return undefined;
    }
    const record = hold;
    const text = (value) => typeof value === 'string' && value ? value : undefined;
    return {
        role: record.role === 'client' || record.role === 'interpreter' ? record.role : undefined,
        callId: text(record.callId),
        roomName: text(record.roomName) || null,
        device: text(record.device),
        since: text(record.since) || null,
        canMoveHere: record.canMoveHere === true
    };
}
/** Consistent recovery guidance without assuming this room came from a queue. */
export function getRoomJoinFailureMessage(error) {
    if (error instanceof RoomAuthorizationError) {
        if (error.code === 'GUEST_SESSION_REQUIRED') {
            return 'Reopen your invitation in the browser where you first joined. Allow cookies.';
        }
        if (error.code === 'INVITE_NOT_AVAILABLE') {
            return 'Invitation unavailable. Ask your host for a new one.';
        }
        if (error.code === 'GUEST_ACCESS_UNAVAILABLE') {
            return 'Couldn’t reconnect. Try again.';
        }
        if (error.code === 'SIGN_IN_REQUIRED') {
            return 'Sign in to join this call.';
        }
        if (error.status === 401) {
            return 'Session expired. Sign in again.';
        }
        if (error.code === 'JOB_ROOM_FORBIDDEN') {
            return 'Room limited to the job’s team. Contact dispatch.';
        }
        if (error.status === 403) {
            return 'Room unavailable.';
        }
        if (error.code === 'ROOM_AUTH_UNAVAILABLE' || error.code === 'JOB_ROOM_AUTH_UNAVAILABLE') {
            return 'Couldn’t check room access. Try again.';
        }
        if (error.code === 'JITSI_AUTH_NOT_CONFIGURED') {
            return 'Calling unavailable. Try again later.';
        }
        if (error.code === 'CALL_ACTIVE_ON_OTHER_DEVICE') {
            const device = error.hold?.device || 'another device';
            return `This call is open on ${device}. Reopen the room to move it here.`;
        }
    }
    return 'Unable to join this room. Try again.';
}
export async function addJitsiRoomToken(locationURL, roomName) {
    const guestSessionId = locationURL.searchParams.get('guestSessionId');
    // A guest grant stays a guest grant even when this browser also has an app account.
    // Signed out with no guest grant, only a live token for this exact room
    // already in the URL (an observer's or guest's media-only grant) may
    // enter. Anything else would reach the room unsigned, which the media
    // service refuses (deploy/jitsi/prosody/mod_muc_strict_admission.lua).
    // Web redirects signed-out visitors before this (getRouteToRender.web);
    // this is where a signed-out native app lands from a room link.
    if (!guestSessionId && !getSecureItem('vrs_auth_token')) {
        const carriedToken = locationURL.searchParams.get('jwt');
        if (carriedToken && !isRoomTokenExpired(carriedToken) && roomTokenMatchesRoom(carriedToken, roomName)) {
            return new URL(locationURL.toString());
        }
        throw new RoomAuthorizationError('Sign in to join this call.', 401, 'SIGN_IN_REQUIRED');
    }
    // A URL already carrying a live token for THIS room is already authorized.
    // Minting a second one changes the URL, and on web a changed conference URL
    // is a full page load — which re-enters this function, mints again, and
    // reloads again, forever. Production loop 2026-08-02: both parties cycled
    // every ~3s with a fresh jwt in the address bar each time. Native survived
    // it because in-app navigation never re-runs this on a URL change.
    // Exception: "Move call here" must mint — the mint is what asks the
    // server to move the call — and the flag is dropped from the result, so
    // the next load of the room URL is back under the rule above.
    const moveCall = isMoveCallRequested(locationURL);
    const existingToken = locationURL.searchParams.get('jwt');
    if (!moveCall
        && existingToken
        && !isRoomTokenExpired(existingToken)
        && roomTokenMatchesRoom(existingToken, roomName)) {
        return new URL(locationURL.toString());
    }
    if (guestSessionId) {
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(guestSessionId)) {
            throw new RoomAuthorizationError('Open your original invitation.', 401, 'GUEST_SESSION_REQUIRED');
        }
        const result = await apiClient.post(`/api/vri/guest-sessions/${encodeURIComponent(guestSessionId)}/token`, { roomName }, { auth: 'none' });
        if (result.error)
            throw new RoomAuthorizationError(result.error, result.status, result.code);
        const response = unwrapApiResponse(result);
        if (!response || typeof response.token !== 'string' || !response.token
            || response.roomName !== roomName || response.guestSessionId !== guestSessionId
            || !Number.isFinite(response.expiresInSeconds) || response.expiresInSeconds <= 0
            || !roomTokenMatchesRoom(response.token, roomName) || isRoomTokenExpired(response.token)) {
            throw new RoomAuthorizationError('Secure guest access could not be confirmed.', 503, 'GUEST_ACCESS_UNAVAILABLE');
        }
        const securedURL = new URL(locationURL.toString());
        securedURL.searchParams.set('jwt', response.token);
        return securedURL;
    }
    // The stable browser id lets the server tell a second device of this
    // account apart from the one holding its call (409, or a move).
    const result = await apiClient.post('/api/auth/jitsi-token', {
        roomName,
        deviceId: getHandoffDeviceId(),
        ...moveCall ? { moveCall: true } : {}
    }, { includeErrorBody: true });
    if (result.error) {
        throw new RoomAuthorizationError(result.error, result.status, result.code, result.code === 'CALL_ACTIVE_ON_OTHER_DEVICE' ? holdFromErrorBody(result.errorBody) : undefined);
    }
    const response = unwrapApiResponse(result);
    if (!response.token || response.roomName !== roomName) {
        throw new Error('Secure room token response did not match the requested room.');
    }
    const securedURL = withoutMoveCall(locationURL);
    securedURL.searchParams.set('jwt', response.token);
    return securedURL;
}
